import React, { useEffect, useState, useRef } from "react";
import * as XLSX from "xlsx";
import { useNavigate, useLocation } from 'react-router-dom';
import { saveAs } from "file-saver";
import "../../../styles/Auth/inventory.css";


import { toast } from "react-toastify";

import { getData } from '../../../webService/webservice.js';
import { useStore } from "../../../Context/context.jsx";
import { Typography, Box, Button } from "@mui/material";

import { Icon } from "@iconify/react";
import StoreSingleAllStoresSelect, { ALL_STORE_OPTION, STORE_OPTION } from "../../Challange/StoreSingleAllStoresSelect.jsx";
import Footer from "../Footer/Footer-page.jsx";

const InventoryPage = () => {

    const sessionToken = localStorage.getItem("Token");
    const role = localStorage.getItem("role");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [inventories, setInventories] = useState([]);
    const [limit, setLimit] = useState(5);
    const [exporting, setExporting] = useState(false);
    const adminStoreIds = JSON.parse(localStorage.getItem("storeIds") || "[]");
    const [store, setStore] = useState(ALL_STORE_OPTION);
    const initialStoreId = adminStoreIds[0] || "";
    const { selectedStoreId, setSelectedStoreId } = useStore();

    useEffect(() => {
        const sid = ["SuperAdmin", "SelfAdmin", "Manager", "Accountant"].includes(role)
            ? store?.value
            : (Array.isArray(selectedStoreId) ? selectedStoreId[0] : selectedStoreId);

        if (sid) {
            fetchInventory(sid);
        }
    }, [selectedStoreId, currentPage, limit, store, role, searchQuery]);




    const fetchInventory = async (storeId) => {
        try {
            setLoading(true);

            if (!storeId) {
                setInventories([]);
                setTotalCount(0);
                setTotalPages(1);
                return;
            }

            const queryLimit = limit === "all" ? "all" : Number(limit);
            const searchParam = searchQuery ? `&search=${searchQuery}` : "";

            const response = await getData(
                `inventories/products/${storeId}?page=${currentPage}&limit=${queryLimit}${searchParam}`,
                {},
                sessionToken
            );

            const resData = response?.data || {};

            setInventories(resData.paginatedResults || []);
            setTotalCount(resData.totalCount || 0);
            setTotalPages(resData.totalPages || 1);

        } catch (error) {
            toast.error("Failed to fetch inventory");
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        const handleStoreChange = () => {
            const storeId = selectedStoreId;
            setSelectedStoreId(storeId);
            setCurrentPage(1);
        };

        window.addEventListener("storeChanged", handleStoreChange);
        return () => window.removeEventListener("storeChanged", handleStoreChange);
    }, []);


    const handleExportExcel = async () => {
        const sid = ["SuperAdmin", "SelfAdmin", "Manager", "Accountant"].includes(role)
            ? store?.value
            : (Array.isArray(selectedStoreId) ? selectedStoreId[0] : selectedStoreId);

        let dataToExport = inventories;

        // If currently viewing a paginated subset (not "all"), fetch all records for full export
        if (limit !== "all" && sid && totalCount > inventories.length) {
            try {
                setExporting(true);
                toast.info("Preparing complete inventory export...");
                const searchParam = searchQuery ? `&search=${searchQuery}` : "";
                const response = await getData(
                    `inventories/products/${sid}?page=1&limit=all${searchParam}`,
                    {},
                    sessionToken
                );
                const resData = response?.data || {};
                dataToExport = resData.paginatedResults || [];
            } catch (error) {
                console.error("Export fetch error:", error);
                toast.error("Failed to fetch full inventory for export");
            } finally {
                setExporting(false);
            }
        }

        if (!dataToExport || dataToExport.length === 0) {
            toast.info("No data to export");
            return;
        }

        const exportData = dataToExport.map(item => ({
            "Product Title": item.title || "N/A",
            "UPC/SKU": item.upc || "N/A",
            "In Stock": item.inStock || 0,
            "Sold": item.sold || 0,
            "Purchased": item.purchased || 0,
            "Returned": item.returned || 0,
            "Self Used": item.selfUsed || 0
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Inventory");
        XLSX.writeFile(workbook, `Inventory_Report_${new Date().toLocaleDateString('en-GB').replace(/\//g, '-')}.xlsx`);
    };

    const handlePageChange = (direction) => {
        setCurrentPage((prev) => {
            if (direction === "first") return 1;
            if (direction === "last") return totalPages;
            if (direction === "next") return Math.min(prev + 1, totalPages);
            if (direction === "prev") return Math.max(prev - 1, 1);
            return prev;
        });
    };

    const handleItemsPerPageChange = (e) => {
        const value = e.target.value;
        const newLimit = value === "all" ? "all" : Number(value);
        setLimit(newLimit);
        setCurrentPage(1);
    };

    return (
        <div className="product-layout">
            <div className="product-content">
                <main className="product-main">
                    <Typography variant="h6" gutterBottom>
                        Inventories
                    </Typography>

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                        <div style={{ flex: 1 }}>
                            {["SuperAdmin", "SelfAdmin", "Manager", "Accountant"].includes(role) && (
                                <StoreSingleAllStoresSelect
                                    value={store}
                                    onChange={(opt) => setStore(opt)}
                                    showAll={false}
                                />
                            )}
                        </div>
                        <button
                            className="add-Product-button"
                            onClick={handleExportExcel}
                            disabled={exporting}
                            style={{ display: "flex", alignItems: "center", gap: "8px" }}
                        >
                            <Icon icon="mdi:file-excel" width="20" />
                            {exporting ? "Exporting..." : "Export Inventory"}
                        </button>
                    </div>


                    <div className="inventory-controls">
                        <div className="inventory-stats">
                            <div className="stat-card">
                                <div className="stat-icon theme">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
                                        <path d="M3 3v18h18" />
                                        <path d="M18 17V9" />
                                        <path d="M13 17V5" />
                                        <path d="M8 17v-3" />
                                    </svg>
                                </div>
                                <div className="stat-info">
                                    <div className="stat-label">Total Products</div>
                                    <div className="stat-value">{totalCount}</div>
                                </div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-icon theme">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
                                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                                        <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                                        <line x1="12" y1="22.08" x2="12" y2="12" />
                                    </svg>
                                </div>
                                <div className="stat-info">
                                    <div className="stat-label">In Stock</div>
                                    <div className="stat-value">{inventories.reduce((sum, item) => sum + (item.inStock || 0), 0)}</div>
                                </div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-icon theme">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10" />
                                        <path d="M12 6v6l4 2" />
                                    </svg>
                                </div>
                                <div className="stat-info">
                                    <div className="stat-label">Total Sold</div>
                                    <div className="stat-value">{inventories.reduce((sum, item) => sum + (item.sold || 0), 0)}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="inventory-card-grid">
                        {loading && (
                            <div className="loading-overlay">
                                <div className="loading-spinner"></div>
                                <p>Loading inventory...</p>
                            </div>
                        )}
                        {inventories.length === 0 ? (
                            <p className="no-data">No Inventory Found</p>
                        ) : (
                            inventories.map((item, index) => (
                                <div key={index} className="inv-card">
                                    <div className="inv-card-header">
                                        <h3>{item.title}</h3>
                                        <div className="product-sku">UPC: {item.upc || 'N/A'}</div>
                                    </div>

                                    <div className="inv-card-image">
                                        <div className="image-container">
                                            <img
                                                src={
                                                    item.productImageObject?.uri
                                                        ? `${process.env.REACT_APP_API_URL}${item.productImageObject.uri}`
                                                        : "/assets/placeholder.png"
                                                }
                                                alt={item.productName || "Product"}
                                                onError={(e) => {
                                                    e.target.src = "/assets/placeholder.png";
                                                }}
                                            />
                                            <div className={`stock-badge ${item.inStock > 0 ? 'in-stock' : 'out-of-stock'}`}>
                                                {item.inStock > 0 ? `${item.inStock} units` : 'Out of Stock'}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="inv-card-body">
                                        <div className="metrics-grid">
                                            <div className="metric-item sold">
                                                <div className="metric-icon theme">
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
                                                        <path d="M9 11l3 3L22 4" />
                                                        <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
                                                    </svg>
                                                </div>
                                                <div className="metric-info">
                                                    <span className="metric-label">Sold</span>
                                                    <strong className="metric-value">{item.sold || 0}</strong>
                                                </div>
                                            </div>

                                            <div className="metric-item purchased">
                                                <div className="metric-icon theme">
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
                                                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                                                        <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                                                        <line x1="12" y1="22.08" x2="12" y2="12" />
                                                    </svg>
                                                </div>
                                                <div className="metric-info">
                                                    <span className="metric-label">Purchased</span>
                                                    <strong className="metric-value">{item.purchased || 0}</strong>
                                                </div>
                                            </div>

                                            <div className="metric-item returned">
                                                <div className="metric-icon theme">
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
                                                        <path d="M3 7v6h6" />
                                                        <path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13" />
                                                    </svg>
                                                </div>
                                                <div className="metric-info">
                                                    <span className="metric-label">Returned</span>
                                                    <strong className="metric-value">{item.returned || 0}</strong>
                                                </div>
                                            </div>

                                            <div className="metric-item self-used">
                                                <div className="metric-icon theme">
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
                                                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                                        <circle cx="12" cy="7" r="4" />
                                                    </svg>
                                                </div>
                                                <div className="metric-info">
                                                    <span className="metric-label">Self Used</span>
                                                    <strong className="metric-value">{item.selfUsed || 0}</strong>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="stock-highlight">
                                            <div className="stock-info">
                                                <span className="stock-label">Current Stock</span>
                                                <strong className={`stock-value ${item.inStock > 0 ? 'positive' : 'negative'}`}>
                                                    {item.inStock || 0}
                                                </strong>
                                            </div>
                                            <div className="stock-indicator">
                                                <div className="progress-bar">
                                                    <div
                                                        className={`progress-fill ${item.inStock > 10 ? 'good' : item.inStock > 0 ? 'low' : 'critical'}`}
                                                        style={{ width: `${Math.min(100, (item.inStock / 50) * 100)}%` }}
                                                    ></div>
                                                </div>
                                                <span className="stock-status">
                                                    {item.inStock > 10 ? 'Well Stocked' : item.inStock > 0 ? 'Low Stock' : 'Critical'}
                                                </span>
                                            </div>
                                        </div>

                                    </div>


                                </div>
                            ))
                        )}
                    </div>
                    <div style={{ display: "flex", justifyContent: "center", marginTop: 20 }}>
                        <div className="product-bottom">
                            <div className="product-section show-entries">
                                <span className="text-label">Show</span>
                                <select className="entries-select" value={limit}
                                    onChange={handleItemsPerPageChange}
                                >
                                    <option value="5">5</option>
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="all">All</option>
                                </select>
                                <span className="text-label">Entries</span>
                            </div>
                            <div className="product-section showing-range">
                                {
                                    limit === 'all'
                                        ? `Showing all ${totalCount} entries`
                                        : `Showing ${(currentPage - 1) * limit + 1} to ${Math.min(currentPage * limit, totalCount)} of ${totalCount} entries`
                                }
                            </div>
                            <div className="product-section spacer"></div>
                            <div className="product-section pagination-buttons">
                                <button
                                    className="pagination-btn"
                                    disabled={currentPage === 1}
                                    onClick={() => handlePageChange("first")}
                                >
                                    <Icon icon="mdi:chevron-double-left" />
                                </button>
                                <button
                                    className="pagination-btn"
                                    disabled={currentPage === 1}
                                    onClick={() => handlePageChange("prev")}
                                >
                                    <Icon icon="mdi:chevron-left" />
                                </button>
                                <button
                                    className="pagination-btn"
                                    disabled={currentPage === totalPages}
                                    onClick={() => handlePageChange("next")}
                                >
                                    <Icon icon="mdi:chevron-right" />
                                </button>
                                <button
                                    className="pagination-btn"
                                    disabled={currentPage === totalPages}
                                    onClick={() => handlePageChange("last")}
                                >
                                    <Icon icon="mdi:chevron-double-right" />
                                </button>
                            </div>
                        </div>
                    </div>
                </main>
                <Footer />
            </div>


        </div>
    );
};


export default InventoryPage;
